
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboarding } from '@/hooks/useOnboarding';
import { getStepById } from '@/components/onboarding/config/onboardingSteps';
import { WelcomeStep } from '@/components/onboarding/steps/WelcomeStep';
import { ProfileAndOrganizationSetupStep } from '@/components/onboarding/steps/ProfileAndOrganizationSetupStep';
import { FeatureTourStep } from '@/components/onboarding/steps/FeatureTourStep';
import { AgentActivationStep } from '@/components/onboarding/steps/AgentActivationStep';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

const Onboarding: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const {
    progress,
    isLoading,
    needsOnboarding,
    initializeOnboarding,
    completeStep,
    skipStep,
  } = useOnboarding();

  // Initialize onboarding for new users
  useEffect(() => {
    if (!authLoading && user && needsOnboarding) {
      console.log('[Onboarding] Initializing onboarding for new user');
      initializeOnboarding.mutate();
    }
  }, [user, needsOnboarding, authLoading, initializeOnboarding]);

  // Handle completion - redirect to dashboard
  useEffect(() => {
    if (progress?.is_completed) {
      console.log('[Onboarding] Onboarding completed, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [progress?.is_completed, navigate]);

  // Loading state
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Loading onboarding...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No authenticated user
  if (!user) {
    navigate('/auth', { replace: true });
    return null;
  }

  // No progress yet (shouldn't happen if initialization works)
  if (!progress) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <p className="text-muted-foreground">Setting up onboarding...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStep = getStepById(progress.current_step);
  
  if (!currentStep) {
    console.error('[Onboarding] Current step not found:', progress.current_step);
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <p className="text-destructive">Onboarding configuration error</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleComplete = async () => {
    console.log('[Onboarding] Completing step:', currentStep.id);
    completeStep.mutate(currentStep.id);
  };

  const handleSkip = async () => {
    if (currentStep.isSkippable) {
      console.log('[Onboarding] Skipping step:', currentStep.id);
      skipStep.mutate(currentStep.id);
    }
  };

  const renderStep = () => {
    const stepProps = {
      onComplete: handleComplete,
      isLoading: completeStep.isPending || skipStep.isPending,
    };

    const skippableStepProps = {
      ...stepProps,
      onSkip: handleSkip,
    };

    switch (currentStep.id) {
      case 'welcome':
        return (
          <WelcomeStep 
            onComplete={stepProps.onComplete}
            isLoading={stepProps.isLoading}
          />
        );
      case 'profile_and_organization':
        return (
          <ProfileAndOrganizationSetupStep 
            onComplete={stepProps.onComplete}
            isLoading={stepProps.isLoading}
          />
        );
      case 'feature_tour':
        return (
          <FeatureTourStep 
            onComplete={skippableStepProps.onComplete}
            onSkip={skippableStepProps.onSkip}
            isLoading={skippableStepProps.isLoading}
          />
        );
      case 'agent_activation':
        return (
          <AgentActivationStep 
            onComplete={skippableStepProps.onComplete}
            onSkip={skippableStepProps.onSkip}
            isLoading={skippableStepProps.isLoading}
          />
        );
      default:
        return (
          <div className="text-center">
            <p className="text-destructive">Unknown step: {currentStep.id}</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">
                Step {progress.completed_steps.length + 1} of 4
              </span>
              <span className="text-sm text-gray-500">
                {currentStep.estimated_time_minutes} min remaining
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300" 
                style={{ 
                  width: `${((progress.completed_steps.length) / 4) * 100}%` 
                }}
              />
            </div>
          </div>

          {/* Step content */}
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              {renderStep()}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
